import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Link from '@mui/material/Link'
import Typography from '@mui/material/Typography'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import ShareIcon from '@mui/icons-material/Share'
import { copyResourceLink } from '../modals/copyResourceLink'
import { youtubeEmbed } from '../../utils/youtubeEmbed'
import { safeHref } from '../../utils/safeUrl'
import ResourceDescription from '../shared/ResourceDescription'
import { TCAIREMStyle } from '../styles'

const NAVBAR_OFFSET = 80

// Descriptions at or under this length fit the 4-line clamp comfortably, so
// the metadata column may take up to half the row instead of a fixed 280px.
const WIDE_META_DESCRIPTION_LIMIT = 200

export const metaVariant = (description) => {
    const text = (description || '').trim()
    if (!text) return 'full'
    return text.length <= WIDE_META_DESCRIPTION_LIMIT ? 'wide' : 'default'
}

const META_VARIANT_STYLES = {
    full: 'rowMetaFull',
    wide: 'rowMetaWide',
    default: 'rowMetaDefault',
}

/**
 * One resource line inside a T-CAIREM type section: metadata column,
 * optional video poster, and description. When `highlighted` (deep link),
 * scrolls itself into view under the fixed navbar — but never auto-opens
 * the player (no iframe may exist before the visitor clicks play).
 */
const TCAIREMResourceRow = ({ resource, alternate, highlighted }) => {
    const dispatch = useDispatch()
    const rowRef = useRef(null)
    const posterRef = useRef(null)
    const playerRef = useRef(null)
    const [playerOpen, setPlayerOpen] = useState(false)
    // Previous committed value of playerOpen, so the focus effect below can tell
    // an open/close transition from the initial mount (same shape as
    // prevForceShowErrorsRef in tags/TagSelector.js:51).
    const prevPlayerOpenRef = useRef(false)
    const embed = youtubeEmbed(resource.link)

    useEffect(() => {
        if (!(highlighted && rowRef.current)) return
        // Delay until the owning section's Collapse animation finishes so the
        // measured position is the row's final one (same approach as Start.js).
        const timer = setTimeout(() => {
            if (!rowRef.current) return
            const top = rowRef.current.getBoundingClientRect().top + window.scrollY - NAVBAR_OFFSET
            window.scrollTo({ top, behavior: 'smooth' })
        }, 300)
        return () => clearTimeout(timer)
    }, [highlighted])

    useEffect(() => {
        // Opening swaps the poster button out for the player region and closing
        // swaps it back. Either way the node that held focus unmounts and focus
        // falls to <body>, stranding the keyboard user (issue #330). Move it
        // deliberately instead.
        //
        // The previous-value guard is the whole point: TCAIREMSection wraps its
        // rows in <Collapse mountOnEnter>, so a section's rows all mount at once
        // and an unguarded effect would let the last one steal page focus. On the
        // first render prev === playerOpen, so neither branch runs.
        //
        // preventScroll: the fixed navbar is why this file carries NAVBAR_OFFSET
        // and the scroll effect above. Opening reflows the row from `row` to
        // `column` (rowPlayerOpen), so the region is much taller than the poster
        // it replaced — often taller than the viewport minus the navbar, which
        // means no scroll position can show all of it at once. We deliberately
        // keep the region's top where the poster already was: below the navbar,
        // with the Close button — the region's first child, and the next Tab
        // stop — visible. The browser's default focus-scroll would instead bring
        // the region's bottom into view and tuck that top under the navbar. The
        // accepted cost is that a tall region's lower portion may sit below the
        // fold. Do not "simplify" it away.
        const wasOpen = prevPlayerOpenRef.current
        if (playerOpen && !wasOpen) {
            playerRef.current?.focus({ preventScroll: true })
        } else if (wasOpen && !playerOpen) {
            // Freshly mounted node: posterRef has already been re-attached by the
            // time this passive effect runs.
            posterRef.current?.focus({ preventScroll: true })
        }
        prevPlayerOpenRef.current = playerOpen
    }, [playerOpen])

    const variant = metaVariant(resource.description)

    // Staff-authored today, but the same missing boundary as the automation review
    // UI (issue #404) -- guard it here too rather than trust resource.link directly.
    const linkHref = safeHref(resource.link)

    const handleShare = () => copyResourceLink({
        dispatch,
        resourcePath: 'tcairem-resources',
        resourceId: resource.id,
    })

    return (
        <Box
            ref={rowRef}
            sx={{
                ...TCAIREMStyle.row,
                ...(alternate ? TCAIREMStyle.rowAlternate : {}),
                ...(highlighted ? TCAIREMStyle.rowHighlighted : {}),
                ...(playerOpen ? TCAIREMStyle.rowPlayerOpen : {}),
            }}
        >
            <Box
                sx={{
                    ...TCAIREMStyle.rowMeta,
                    ...(playerOpen
                        ? TCAIREMStyle.rowMetaHeader
                        : TCAIREMStyle[META_VARIANT_STYLES[variant]]),
                }}
            >
                <Typography variant='h6' component='p'>{resource.title}</Typography>
                {resource.pub_date && <Typography variant='body2'>{`Published: ${resource.pub_date}`}</Typography>}
                {resource.authors && <Typography variant='body2'>{resource.authors}</Typography>}
                {linkHref && (
                    <Box sx={TCAIREMStyle.rowLink}>
                        <Link
                            href={linkHref}
                            target='_blank'
                            rel='noopener noreferrer'
                            aria-label={`Link: ${resource.title}`}
                        >
                            Link
                        </Link>
                        {resource.is_paid && <Chip size='small' color='warning' label='Paid for institutions' />}
                    </Box>
                )}
                {resource.doi && <Typography variant='body2'>DOI: {resource.doi}</Typography>}
                <Button size='small' startIcon={<ShareIcon />} onClick={handleShare}>
                    Share this resource
                </Button>
            </Box>
            {embed && (playerOpen ? (
                <Box
                    ref={playerRef}
                    role='group'
                    aria-label={`${resource.title} video player`}
                    tabIndex={-1}
                    sx={TCAIREMStyle.rowPlayer}
                >
                    <Button size='small' onClick={() => setPlayerOpen(false)}>Close video</Button>
                    {/* YouTube answers an embed request that carries no usable Referer with
                        "Error 153". A browser configured to strip the cross-origin Referer by
                        default (Firefox network.http.referer.defaultPolicy=0) causes exactly
                        that (issue #325). This attribute overrides the browser default and
                        restores the header. public/index.html's <meta name="referrer"> tag
                        (issue #332) sets the same value document-wide, but this attribute
                        overrides that too — were it removed, the tag would supply the
                        same value to this iframe, so removing it would not by itself bring
                        Error 153 back. Keep it anyway, so an edit to index.html can never
                        silently change this embed's policy. */}
                    <Box
                        component='iframe'
                        src={`${embed.embedUrl}?autoplay=1`}
                        title={resource.title}
                        allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
                        allowFullScreen
                        referrerPolicy='strict-origin-when-cross-origin'
                        sx={TCAIREMStyle.rowPlayerIframe}
                    />
                    {/* A browser can also strip the cross-origin Referer at the network level
                        (Firefox network.http.referer.XOriginPolicy=2), before any attribute
                        applies — no site-side change can fix that, and the iframe is
                        cross-origin so the page cannot detect the failure and swap itself out.
                        Hence an always-present way off a dead player.
                        No aria-label: the visible text is the accessible name, keeping it
                        distinct from the metadata link's "Link: <title>". */}
                    <Link
                        href={linkHref}
                        target='_blank'
                        rel='noopener noreferrer'
                        variant='body2'
                    >
                        Watch on YouTube
                    </Link>
                </Box>
            ) : (
                <Box
                    ref={posterRef}
                    component='button'
                    type='button'
                    aria-label={`Play ${resource.title}`}
                    onClick={() => setPlayerOpen(true)}
                    sx={TCAIREMStyle.rowPosterButton}
                >
                    {/* The thumbnail needs no Referer, so send none, matching the
                        ResourceCard thumbnail (#430). The iframe above is the
                        opposite: YouTube requires one there. */}
                    <Box
                        component='img'
                        src={embed.posterUrl}
                        alt={resource.title}
                        loading='lazy'
                        referrerPolicy='no-referrer'
                        sx={TCAIREMStyle.rowPosterImage}
                    />
                    <PlayCircleOutlineIcon sx={TCAIREMStyle.rowPosterPlayIcon} />
                </Box>
            ))}
            <ResourceDescription
                description={resource.description}
                containerSx={TCAIREMStyle.rowBody}
                remeasureKey={playerOpen}
            />
        </Box>
    )
}

export default TCAIREMResourceRow
